import React from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { Machine } from 'xstate'
import { numberise, pixelise, setAlpha } from '@jarvis.oddle/core/tools/style'
import {
  borderRadiusSizes,
  colorConstants,
  colors,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Div from '@jarvis.oddle/core/components/Div'
import DivButton from '@jarvis.oddle/core/components/DivButton'
import Flex from '@jarvis.oddle/core/components/Flex'
import IconTrackLocation from '@jarvis.oddle/core/components/Icons/IconTrackLocation'
import IconMapDragPin from '@jarvis.oddle/core/components/Icons/IconMapDragPin'
import Form, { withFormContext } from '@jarvis.oddle/core/components/Form'
import { H4, P, SmallText } from '@jarvis.oddle/core/components/Typo'
import Collapsible from '@jarvis.oddle/core/components/Collapsible'
import Divider from '@jarvis.oddle/core/components/Divider'
import { Portal } from '@jarvis.oddle/core/components/Portal'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import MapService from 'src/containers/MapService'
import AddressBookCard from 'src/components/molecules/AddressBookCard'
import breakpoint from 'src/utils/styledComponentsBreakpoint'

const isAddressOneMissing = (deliveryAddress) =>
  deliveryAddress && !deliveryAddress.addressOne

const AddressSearchContainer = styled(Flex).attrs(() => ({
  flexDirection: 'column'
}))`
  ${breakpoint('desktop')`
    min-height: 500px;
  `}
  flex-grow: 1;
`

// Style fix for IE
const MapGlobalStyle = createGlobalStyle`
  html.__browser.IE {
    .__addressSearchMapWrapper {
      width: 100%;
      transform: translateX(0);
    }
  }
`

const MapWrapper = styled(Flex).attrs(() => ({
  flexGrow: 1,
  className: '__addressSearchMapWrapper'
}))`
  position: relative;
  width: calc(100% + ${pixelise(numberise(padding[paddingSizes.small]) * 2)});
  transform: translateX(-${padding[paddingSizes.small]});
  ${breakpoint('desktop')`
    width: calc(100% + ${pixelise(
      numberise(padding[paddingSizes.medium]) * 2
    )});
    transform: translateX(-${padding[paddingSizes.medium]});
  `}
`

const Fade = styled(Div)`
  position: absolute;
  top: 0;
  left: 0;
  height: 30px;
  width: 100%;
  background-image: linear-gradient(
    to top,
    rgba(255, 255, 255, 0.01),
    ${(props) => props.theme.colorContainerColor || colors.container}
  );
`

const MapContainer = styled(Div)`
  flex-grow: 1;
`

const MapInstructionContainer = styled(Div).attrs(() => ({
  borderRadius: borderRadiusSizes.small,
  paddingLeft: paddingSizes.small,
  paddingRight: paddingSizes.small
}))`
  position: absolute;
  top: ${padding[paddingSizes.medium]};
  left: 50%;
  transform: translateX(-50%);
  background-color: ${setAlpha(colorConstants.black, 0.7)};
  text-align: center;
  pointer-events: none;
  user-select: none;
`

const MapMarker = styled(IconMapDragPin)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-100%);
`

const UseCurrentLocationContainer = styled(DivButton).attrs(() => ({
  marginTop: marginSizes.small
}))`
  display: flex;
  align-self: flex-start;
  align-items: center;
`

const SubmitButton = withFormContext(
  ({ form: { values, errors }, loading, onClick, text, type }) => (
    <Form.SubmitButton
      data-testid='addressSearch.submitBtn'
      type={type}
      fullWidth
      onClick={onClick}
      disabled={
        errors.deliveryAddress ||
        (!values.selectedSavedAddress &&
          (!values.deliveryAddress ||
            isAddressOneMissing(values.deliveryAddress))) ||
        loading
      }
    >
      {text}
    </Form.SubmitButton>
  )
)

const AddressCardContainer = styled(Flex).attrs(() => ({
  borderRadius: 'small',
  paddingTop: 'small',
  paddingBottom: 'small',
  paddingLeft: 'medium',
  paddingRight: 'medium',
  marginTop: 'small',
  alignItems: 'center'
}))`
  border: 1px
    ${(props) =>
      props.error
        ? colorConstants.red
        : props.theme.colorLinesAndBorders || colors.separator}
    solid;
  cursor: pointer;
`

const AddressSearch = ({
  form,
  countryCode,
  flow,
  getMapProps,
  mapApi,
  mapUtils,
  MapComponent,
  MapServiceLogo,
  savedAddresses = [],
  validator = () => { return { valid: true } },
  submitButtonText,
  hideAddressTwo
}: {
  form: {
    errors: Record<string, string>
    handleSubmit: () => void
    values: Record<string, unknown>
  }
  countryCode
  flow
  getMapProps
  mapApi
  mapUtils
  MapComponent
  MapServiceLogo
  savedAddresses
  validator: (values) => { valid: boolean }
  submitButtonText
  hideAddressTwo
}) => {
  const { t } = React.useContext(TranslationContext)
  const flowRef = React.useRef<{
    transition
    initialState
  }>(Machine(flow))
  const [flowState, setFlowState] = React.useState(
    flowRef.current.initialState.value
  )

  const transition = (event) => {
    const nextFlowState = flowRef.current.transition(flowState, event).value
    setFlowState(nextFlowState)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (flowState === 'map') {
      transition('NEXT')
      return
    }
    form.handleSubmit()
  }

  const onDropdownClosed = () => transition('CLOSE_DROPDOWN')

  const onDropdownShow = () => transition('SHOW_DROPDOWN')

  const onAddressSelected = () => transition('SELECT_ADDRESS')

  const onReset = () => transition('RESET')

  const onSearchStringUpdate = ({ searchString }) =>
    searchString === '' ? transition('QUERY_CLEAR') : transition('QUERY_CHANGE')

  const validate = async (selected) => {
    // NOTE: Don't show form errors if user selects an address without addressOne
    // Only happens for search results returned by Google Place Autocomplete API
    // Refer to https://oddle-pm.atlassian.net/browse/FESC-1275
    if (isAddressOneMissing(selected)) {
      return { valid: true }
    }
    return validator(selected)
  }

  return (
    <AddressSearchContainer>
      <MapGlobalStyle />
      <Form.AddressSearchInput
        name='deliveryAddress'
        MapServiceLogo={MapServiceLogo}
        searchApi={mapApi.findPlaceFromQuery}
        resultParser={(x) => x.formattedAddress}
        onSearchStringUpdate={onSearchStringUpdate}
        onSearchResultSelected={onAddressSelected}
        onSearchResultsUpdate={onDropdownShow}
        onSearchResultsShow={onDropdownShow}
        onSearchResultsClosed={onDropdownClosed}
        initialSelected={form.values.deliveryAddress || {}}
        onReset={onReset}
        validator={validate}
      >
        {({ selected, loading, actions: searchActions }) => {
          const onMapCenterChange = () =>
            searchActions.searchAndSetSelected(mapUtils.getMapCenterAddress)
          const setCurrentAddress = async () => {
            let currentAddress
            if (flowState === 'map') {
              // NOTE: When map is displayed, only retrieve current address and rely on mapUtils.setMapCenter(currentAddress) to set selected address in the search input
              // This's to prevent calling backend api twice (as selected address is also set if we call searchActions.searchAndSetSelected)
              try {
                searchActions.setSearchLoading()
                currentAddress = await mapApi.getCurrentAddress()
              } finally {
                searchActions.setSearchLoaded()
              }
            } else {
              currentAddress = await searchActions.searchAndSetSelected(
                mapApi.getCurrentAddress
              )
            }
            if (currentAddress) {
              mapUtils.setMapCenter(currentAddress)
              transition('SELECT_ADDRESS')
            }
          }
          return (
            <React.Fragment>
              {MapComponent && flowState !== 'addressTwo' && (
                <UseCurrentLocationContainer onClick={setCurrentAddress}>
                  <IconTrackLocation fill={colors.alternativeHighlightColor} />
                  <P
                    color={colors.alternativeHighlightColor}
                    marginLeft='small'
                    normalised
                  >
                    {t('Use my current address')}
                  </P>
                </UseCurrentLocationContainer>
              )}
              {flowState === 'map' && selected && (
                <MapWrapper>
                  <MapComponent
                    {...getMapProps()}
                    Container={MapContainer}
                    initialCenter={selected}
                    onDragEnd={onMapCenterChange}
                    onZoomChanged={onMapCenterChange}
                  />
                  <Fade />
                  <MapInstructionContainer>
                    <SmallText color={colorConstants.white} normalised>
                      {flowState === 'map' &&
                      isAddressOneMissing(selected)
                        ? t('Drag map to your exact location')
                        : t('Drag map to find your location')}
                    </SmallText>
                  </MapInstructionContainer>
                  <MapMarker />
                </MapWrapper>
              )}
              <Portal channel='AddressSearchSubmitButton'>
                <SubmitButton
                  type={flowState === 'map' ? 'button' : 'submit'}
                  onClick={handleSubmit}
                  loading={loading}
                  text={
                    flowState === 'map' ? t('Next') : submitButtonText
                  }
                />
              </Portal>
            </React.Fragment>
          )
        }}
      </Form.AddressSearchInput>
      <Collapsible
        show={
          !form.errors.deliveryAddress &&
          flowState === 'addressTwo' &&
          !hideAddressTwo
        }
        unmountOnExit
      >
        <Div paddingTop='medium'>
          <Form.Input
            name='addressTwo'
            label={t('Building Name or Unit Number')}
            disableAutocomplete
            fieldValidateOnChange={false}
          />
        </Div>
      </Collapsible>
      <Collapsible
        show={
          (savedAddresses || []).length > 0 && flowState === 'start'
        }
        unmountOnExit
      >
        <Div paddingTop='medium' paddingBottom='medium'>
          <Divider />
          <H4 marginBottom='none' bold>
            {t('Saved addresses')}
          </H4>
          <Form.RadioGroup name='selectedSavedAddress'>
            {(savedAddresses || []).map((address) => (
              <AddressBookCard
                key={address.id}
                Container={AddressCardContainer}
                address={address}
                withActionIcons={false}
                error={form.errors.selectedSavedAddress}
                countryCode={countryCode}
              />
            ))}
          </Form.RadioGroup>
        </Div>
      </Collapsible>
    </AddressSearchContainer>
  )
}

export default withFormContext((props) => (
  <MapService>
    {(mapServiceProps) => <AddressSearch {...props} {...mapServiceProps} />}
  </MapService>
))
